import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, DollarSign, Clock, CheckCircle2, AlertCircle, Loader2,
  CreditCard, TrendingUp, ArrowRight, Sparkles, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function QuickPayModal({ isOpen, onClose, availableBalance, jobId }) {
  const [amount, setAmount] = useState(availableBalance || 0);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [primaryAccount, setPrimaryAccount] = useState(null);
  
  // Quick Pay fee calculation (1.5%, min $0.50, max $15)
  const calculateFee = (amt) => {
    const fee = Math.max(0.50, Math.min(amt * 0.015, 15));
    return Math.round(fee * 100) / 100;
  };
  
  const fee = calculateFee(amount);
  const netAmount = Math.round((amount - fee) * 100) / 100;
  
  const fetchData = useCallback(async () => {
    try {
      const [accountsRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/provider-deposits/accounts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/provider-deposits/quick-pay/history?limit=10`, { headers: getAuthHeaders() })
      ]);
      
      const accounts = accountsRes.data.accounts || [];
      setPrimaryAccount(accounts.find(a => a.is_primary) || accounts[0] || null);
      setHistory(historyRes.data.quick_pays || []);
    } catch (error) {
      console.error('Failed to fetch Quick Pay data:', error);
    }
  }, []);
  
  useEffect(() => {
    if (isOpen) {
      fetchData();
      setAmount(availableBalance || 0);
    }
  }, [isOpen, availableBalance, fetchData]);
  
  const handleQuickPay = async () => {
    if (amount < 5) {
      toast.error('Minimum Quick Pay amount is $5.00');
      return;
    }
    
    if (!primaryAccount) {
      toast.error('No payout account linked. Go to Settings > Payout Methods.');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/provider-deposits/quick-pay`,
        { amount, job_id: jobId },
        { headers: getAuthHeaders() }
      );
      
      toast.success(
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          <span>${response.data.net_amount} sent instantly!</span>
        </div>
      );
      
      fetchData();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Quick Pay failed');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
        data-testid="quick-pay-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Quick Pay</h2>
              <p className="text-white/80 text-sm">Get paid instantly</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Primary Account Info */}
          {primaryAccount ? (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${primaryAccount.color}20` }}
              >
                <CreditCard className="w-5 h-5" style={{ color: primaryAccount.color }} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{primaryAccount.platform_name}</p>
                <p className="text-sm text-gray-500">{primaryAccount.display_identifier}</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl text-red-600">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">No payout account linked</span>
            </div>
          )}
          
          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount to Cash Out
            </label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                className="w-full pl-10 pr-4 py-4 text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-0"
                placeholder="0.00"
                min="5"
                step="0.01"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Minimum $5.00
            </p>
          </div>
          
          {/* Fee Breakdown */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Cash Out Amount</span>
              <span className="font-medium">${amount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-1">
                Instant Transfer Fee
                <span className="text-xs text-gray-400">(1.5%)</span>
              </span>
              <span className="text-red-500">-${fee.toFixed(2)}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between">
              <span className="font-semibold text-gray-900">You'll Receive</span>
              <span className="text-xl font-bold text-green-600">${netAmount.toFixed(2)}</span>
            </div>
          </div>
          
          {/* Instant Indicator */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Sparkles className="w-4 h-4 text-yellow-500" />
            <span>Arrives in seconds</span>
            <Clock className="w-4 h-4 text-green-500" />
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleQuickPay}
              disabled={loading || amount < 5 || !primaryAccount}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-xl hover:from-yellow-500 hover:to-orange-600 transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-200"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Cash Out Now
                </>
              )}
            </button>
          </div>
          
          {/* Quick Pay History Toggle */}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            <TrendingUp className="w-4 h-4" />
            {showHistory ? 'Hide' : 'View'} Quick Pay History
            <ArrowRight className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
          </button>
          
          {/* History Section */}
          {showHistory && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {history.length > 0 ? (
                history.map((pay) => (
                  <div
                    key={pay.payout_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        ${pay.net_amount?.toFixed(2)} to {pay.platform_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(pay.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  </div>
                ))
              ) : (
                <p className="text-center text-sm text-gray-500 py-4">
                  No Quick Pay history yet
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
